import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import HeroSection from "@/components/landing/HeroSection";
import TrustBar from "@/components/landing/TrustBar";
import OccasionsSection from "@/components/landing/OccasionsSection";
import HowItWorksSection from "@/components/landing/HowItWorksSection";

import ReviewsSection from "@/components/ReviewsSection";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CheckCircle, Music } from "lucide-react";

const Index = () => {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Custom Songs for Life's Biggest Moments" />
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-lg">
        Skip to main content
      </a>
      <Header />

      <main id="main-content">
        {/* Hero */}
        <HeroSection />

        {/* How It Works */}
        <HowItWorksSection />

        {/* Final CTA */}
        <section id="start" className="py-10 md:py-14 linen-bg">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
                Ready to Create Something Unforgettable?
              </h2>
              <p className="text-muted-foreground text-lg font-body mb-8">
                Your story matters. Let's turn it into music.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button size="xl" onClick={() => scrollTo("pricing")}>
                  Start Your Song
                </Button>
                <Button
                  variant="outline"
                  size="xl"
                  onClick={() => scrollTo("listen")}
                >
                  Hear a Sample
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Occasions */}
        <OccasionsSection />


        {/* Packages / Pricing */}
        <section id="pricing" aria-labelledby="pricing-heading" className="pt-[2.375rem] pb-10 md:pt-[2.875rem] md:pb-14 linen-bg">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12 md:mb-16"
            >
              <h2 id="pricing-heading" className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
                Choose Your Experience
              </h2>
              <div className="section-divider mb-6" aria-hidden="true" />
              <p className="text-muted-foreground font-body max-w-2xl mx-auto text-lg">
                Every custom song is written from your story and designed to be given<br />whether as a heartfelt audio gift or a full video reveal.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
              {/* Essentials */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 md:p-8 border border-border"
              >
                <p className="text-sm font-body uppercase tracking-widest text-muted-foreground mb-2">
                  A Beautifully Crafted Audio Gift
                </p>
                <h3 className="font-heading text-2xl font-bold text-foreground mb-2">
                  Essentials
                </h3>
                <div className="mb-6">
                  <span className="font-heading text-4xl font-bold text-foreground">$35</span>
                </div>
                <ul className="space-y-3 mb-8 font-body">
                  <li className="flex items-start gap-3 text-base">
                    <span className="flex-shrink-0">🎵</span>
                    <span className="text-foreground">Custom-written lyrics inspired by your story</span>
                  </li>
                  <li className="flex items-start gap-3 text-base">
                    <span className="flex-shrink-0">🎧</span>
                    <span className="text-foreground">Custom composed song (MP3)</span>
                  </li>
                  <li className="flex items-start gap-3 text-base">
                    <span className="flex-shrink-0">🌐</span>
                    <span className="text-foreground">English, Spanish, or bilingual options</span>
                  </li>
                  <li className="flex items-start gap-3 text-base">
                    <span className="flex-shrink-0">⏱</span>
                    <span className="text-foreground">Delivered within 48–72 hours</span>
                  </li>
                </ul>
                <Button variant="outline" size="lg" className="w-full font-bold" asChild>
                  <Link to="/create?plan=essentials">Create My Song</Link>
                </Button>
              </motion.div>

              {/* Premium */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="relative rounded-2xl p-6 md:p-8 border-2 border-primary shadow-elevated"
                style={{ backgroundColor: "hsl(40 30% 93%)" }}
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-sm font-semibold font-body px-4 py-1 rounded-full">
                  Most Popular
                </div>
                <p className="text-sm font-body uppercase tracking-widest text-muted-foreground mb-2">
                  The Complete Keepsake Experience
                </p>
                <h3 className="font-heading text-2xl font-bold text-foreground mb-2">
                  Premium + Slideshow
                </h3>
                <div className="mb-4">
                  <span className="font-heading text-4xl font-bold text-foreground">$85</span>
                </div>
                <p className="text-base font-body text-muted-foreground mb-4">
                  Includes everything in Essentials, plus:
                </p>
                <ul className="space-y-3 mb-8 font-body">
                  <li className="flex items-start gap-3 text-base">
                    <span className="flex-shrink-0">🎬</span>
                    <span className="text-foreground">Custom photo slideshow video (MP4)<br />featuring 15–40 images</span>
                  </li>
                  <li className="flex items-start gap-3 text-base">
                    <span className="flex-shrink-0">📄</span>
                    <span className="text-foreground">Digital lyric sheet (PDF)</span>
                  </li>
                  <li className="flex items-start gap-3 text-base">
                    <span className="flex-shrink-0">💝</span>
                    <span className="text-foreground">Designed for emotional reveal moments</span>
                  </li>
                </ul>
                <Button size="lg" className="w-full font-bold transition-all duration-300 hover:scale-[1.04] hover:shadow-elevated active:scale-[0.98]" asChild>
                  <Link to="/create?plan=premium">Create the Full Experience</Link>
                </Button>
              </motion.div>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center text-muted-foreground text-lg font-body mt-8"
            >
              Need it sooner? Add 24-hour rush delivery for +$10.
            </motion.p>

          </div>
        </section>

        {/* Samples section merged into OccasionsSection above */}

        {/* Reviews */}
        <ReviewsSection />

        {/* Emotional CTA */}
        <section className="py-10 md:py-14 bg-primary relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" aria-hidden="true">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.2),transparent_70%)]" />
          </div>

          <div className="container mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-6">
                Some moments are too<br />important for ordinary gifts.
              </h2>
              <p className="text-primary-foreground/80 text-lg font-body mb-8 max-w-xl mx-auto">
                Turn your story into something they can play again and again.
              </p>
              <Button
                variant="gold"
                size="xl"
                onClick={() => scrollTo("start")}
              >
                Start Your Song
              </Button>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
